import React, { Component } from 'react'
import { inject, observer } from 'mobx-react'
import { toJS, action, computed, extendObservable } from 'mobx'
import propTypes from 'prop-types'
import sortBy from 'lodash/sortBy'

import {
  getAttendeeEmails,
  generateLocationLabel,
  generateClientInviteMessage,
  generateCallSubject
} from '../InviteHelpers'
import { getAddressById, searchEmployees, employeeApi, parentAccountApi, projectApi, scheduleApi, teamApi } from '@atheneum/api'

import { formatDateTime, getTimezoneName, shouldHideExpertInfo } from '@atheneum/api/dist/lib'

import { showDialog, isCancellationError } from '@components/Dialog'
import InfoIcon from '@components/Icon/Info'
import InfoBox from '@components/InfoBox'
import { Formik, Form, Field } from 'formik'
import Input from '@components/Form/Input'
import RichTextEditor from '@components/Form/RichTextEditor'
import Checkbox from '@components/Form/Checkbox'
import Spinner from '@components/spinner'
import { Dropdown, TagsInput } from '@components/Form/Autocomplete'
import FormError from '@components/FormError'
import FormLoader from '@components/FormLoader'
import Button from '@components/Button'
import yup from 'yup'

class InviteClient extends Component {
  constructor(props) {
    super(props)
    const { epl, modalStore } = this.props
    const { schedule, zoomMeetingId } = modalStore.props

    extendObservable(this, {
      error: null,
      initialLoading: true,
      submitting: false,
      employeeSubmitted: null,
      address: null,
      clients: [],
      projectClients: [],
      teams: [],
      zoomNumbers: [],
      zoomNumber: null,
      hasZoomMeeting: (schedule && schedule.zoomMeetingId) || zoomMeetingId,
      zoomMeetingId: schedule.zoomMeetingId || zoomMeetingId,
      event: null,
      eventAlreadyExists: computed(() => !!this.event),
      canSkipInvite: computed(() => {
        if (!this.event) return true

        const { organizer, attendees } = this.event
        const invitedAttendees = attendees.filter(attendee => {
          return (
            attendee.emailAddress.address !== organizer.emailAddress.address
          )
        })
        if (invitedAttendees.length === 0) return true

        return false
      }),
      attendees: [],
      parentAccount: null
    })

    this.loadData({ epl, schedule })
  }

  loadData = action(async ({ epl, schedule }) => {
    this.initialLoading = true
    const user = this.props.userStore.user
    const project = this.props.project
    const parentAccountId = project.parentAccountId
    const officeAddressId = project.office.addressId

    try {
      const [
        address,
        projectClients,
        clients,
        zoomNumbers,
        teams,
        officeAddress,
        parentAccount
      ] = await Promise.all([
        getAddressById(user.addressId),
        projectApi.getProjectClients(epl.projectId),
        parentAccountApi.getClients(parentAccountId, { limit: null }),
        scheduleApi.getZoomNumbers(),
        teamApi.getActiveTeams(),
        getAddressById(officeAddressId),
        parentAccountApi.get(parentAccountId)
      ])

      this.address = address
      this.clients.replace(clients)
      const {log} = console
      log('inviteClient this.clients', clients)
      this.projectClients.replace(projectClients)
      this.teams.replace(teams)
      this.parentAccount = parentAccount

      this.zoomNumbers.replace(zoomNumbers)
      const countryId = officeAddress.countryId
      this.zoomNumber = zoomNumbers.find(num => num.countryId === countryId)

      await this.loadEvent(schedule.id)

      if (this.event) {
        const emails = getAttendeeEmails(this.event).filter(
          email => !this.clients.find(c => c.email === email)
        )
        let attendees = []

        const employeeEmails = emails.filter(email =>
          email.endsWith('@atheneum-partners.com')
        )
        if (employeeEmails.length) {
          const { rows: employees } = await searchEmployees({
            emails: employeeEmails
          })
          attendees = attendees.concat(employees)
        }

        const externalEmails = emails.filter(
          email => !email.endsWith('@atheneum-partners.com')
        )
        if (externalEmails.length) {
          const externalAttendees = externalEmails.map(email => ({
            email,
            fullName: email
          }))
          attendees = attendees.concat(externalAttendees)
        }

        this.attendees.replace(attendees)
      }

      this.initialLoading = false
    } catch (error) {
      this.props.growlStore.showError(error)
      console.error(error)
      this.initialLoading = false
    }
  })

  loadEvent = async scheduleId => {
    try {
      const clientEvent = await scheduleApi.getClientEvent(scheduleId)
      if (!clientEvent) return

      this.event = clientEvent
    } catch (error) {
      if (scheduleApi.isUnlinkedOutlookError(error)) {
        return showDialog({
          title: 'Outlook account not linked',
          html: error.message,
          type: 'error'
        })
          .then(() => {
            return this.loadEvent(scheduleId)
          })
          .catch(err => isCancellationError(err))
      } else {
        this.props.growlStore.showError(error)
      }
    }
  }

  returnToScheduleModal = () => {
    this.props.modalStore.show({
      type: 'SCHEDULE_CALENDAR_MODAL',
      props: {
        eplModel: this.props.eplModel,
        epl: this.props.epl,
        project: this.props.project,
        // projectStore: this.props.projectStore
      }
    })
  }

  submitForm = action(async (values, actions) => {
    this.submitting = true
    const { schedule } = this.props

    const {log} = console
    log('inviteClient submitForm', {values, actions, schedule})

    const data = {
      team: values.team,
      message: values.message,
      subject: values.subject,
      location: values.location,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      contacts: values.clients,
      additionalEmails: values.attendees.map(({ email }) => ({ email })),
      timezoneName: getTimezoneName(),
      skipInvite: values.skipInvite,
      attachExpertProfilePdf: values.attachExpertProfilePdf
    }

    const renderSentMessage = skipInvite => {
      return skipInvite ? 'Event added to the Team Calendar' : 'Event sent'
    }

    return scheduleApi.notifyClients(schedule.id, data)
      .then(() => {
        this.submitting = false
        const message = this.eventAlreadyExists
          ? 'Event resent'
          : renderSentMessage(data.skipInvite)
        this.props.growlStore.showSuccess(message)
        this.returnToScheduleModal()
      })
      .catch(error => {
        this.submitting = false

        if (scheduleApi.isUnlinkedOutlookError(error)) {
          return showDialog({
            title: 'Outlook account not linked',
            html: error.message,
            type: 'error'
          }).catch(err => isCancellationError(err))
        } else {
          this.props.growlStore.showError(error)
        }
      })
  })

  getInitialFormValues = () => {
    const { schedule, staticData, epl, userStore } = this.props

    const isExpertNameShown =
      !epl.hideExpertName &&
      this.parentAccount.expertsDisclosed &&
      !shouldHideExpertInfo(this.props.project)

    let subject = 'Atheneum call'
    if (isExpertNameShown) {
      subject = this.event ? this.event.subject : generateCallSubject(schedule)
    }

    const location = this.event
      ? this.event.location.displayName
      : generateLocationLabel(this.zoomNumber, schedule)
    const team = this.props.userStore.teamId

    const attendees = getAttendeeEmails(this.event, { skipOrganizer: true })
    let clientAttendees
    if (this.event) {
      clientAttendees = this.clients.filter(client =>
        attendees.includes(client.email)
      )
    } else {
      clientAttendees = this.projectClients
    }

    const message = this.event
      ? this.event.body.content
      : generateClientInviteMessage({
          staticData,
          epl,
          user: userStore.user,
          address: this.address,
          clients: clientAttendees, // need them
          zoomNumber: this.zoomNumber,
          schedule,
          parentAccount: this.parentAccount
        })

    return {
      team,
      subject,
      location,
      message,
      attendees: toJS(this.attendees),
      clients: toJS(clientAttendees),
      attachExpertProfilePdf: false
    }
  }

  getFormValidationSchema = () => {
    return yup.object().shape({
      team: yup
        .number('Team is required')
        .required('Team is required')
        .nullable(),
      subject: yup
        .string('Subject is required')
        .required('Subject is required'),
      location: yup
        .string('Location is required')
        .required('Location is required'),
      message: yup
        .string('Message is required')
        .required('Message is required'),
      clients: yup
        .array()
        .of(
          yup.object()
          // .shape({ ... })
        )
        .min(1, 'At least one client contact is required'),
      attendees: yup.array().of(
        yup.object().shape({
          email: yup.string().email('Email format is invalid')
        })
      )
    })
  }

  renderTitle = schedule => {
    if (schedule && schedule.outlookClientEventId) {
      return <h1>Update client invitation(s)</h1>
    } else {
      return <h1>Invite client(s)</h1>
    }
  }

  renderMeetingNote = schedule => {
    if (this.hasZoomMeeting) {
      return (
        <div className='request-schedule__section  request-schedule__invite-info'>
          Virtual conference room has been generated on Zoom and details are
          inserted in the event invitation below. If you would like to host a
          call outside of Zoom, just ignore Zoom call details and modify the
          message to match actual information.
        </div>
      )
    } else {
      return (
        <InfoBox className='m-b-lg'>
          <strong>Note:</strong> This schedule has no Zoom meeting!
        </InfoBox>
      )
    }
  }

  renderControls = form => {
    const shouldDisableSubmit = form.isSubmitting

    if (this.canSkipInvite) {
      return (
        <div className='request-schedule__controls'>
          <div className='request-schedule__controls__inner'>
            <Button
              primary
              submit
              onClick={event => {
                event.preventDefault()
                form.setFieldValue('skipInvite', false)
                form.handleSubmit(event)
              }}
              disabled={shouldDisableSubmit}
            >
              Send calendar invitation
            </Button>
            <Button
              secondary
              onClick={event => {
                event.preventDefault()
                form.setFieldValue('skipInvite', true)
                form.handleSubmit(event)
              }}
              disabled={shouldDisableSubmit}
            >
              Only add to team calendar
            </Button>
          </div>
          <Button
            className='button button--secondary'
            onClick={this.returnToScheduleModal}
          >
            Skip calendar invitation
          </Button>
        </div>
      )
    } else {
      return (
        <div className='request-schedule__controls'>
          <div className='request-schedule__controls__inner'>
            <Button
              primary
              submit
              onClick={event => {
                event.preventDefault()
                form.setFieldValue('skipInvite', false)
                form.handleSubmit(event)
              }}
              disabled={shouldDisableSubmit}
            >
              Send calendar invitation
            </Button>
          </div>
          <Button
            className='button button--secondary'
            onClick={this.returnToScheduleModal}
          >
            Skip calendar invitation
          </Button>
        </div>
      )
    }
  }

  getTeamOptions() {
    // the Kanban teamId of -1 is resolved to the correct outlook teamId in schedule.helpers
    let teams = this.teams.slice().concat({
      id: -1,
      name: 'Kanban'
    })
    teams = sortBy(teams, 'name')

    return teams
  }

  render() {
    const { staticData, modalStore } = this.props
    const { schedule } = modalStore.props

    if (this.initialLoading) {
      return <Spinner />
    }

    const { EMPLOYEE_POSITIONS } = staticData.CONST
    const employeePositions = [
      EMPLOYEE_POSITIONS.ASSOCIATE,
      EMPLOYEE_POSITIONS.TEAM_LEADER,
      EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
      EMPLOYEE_POSITIONS.PRINCIPAL
    ]

    return (
      <Formik
        initialValues={this.getInitialFormValues()}
        validationSchema={this.getFormValidationSchema()}
        onSubmit={this.submitForm}
        render={form => (
          <Form onSubmit={form.handleSubmit}>
            <FormLoader loading={form.isSubmitting} />
            <FormError error={this.error} />

            {this.renderTitle(schedule)}

            <hr className='hr-separator hr-separator--light hr-separator--large-margin' />

            {this.renderMeetingNote(schedule)}

            <div className='expert-form__input-group subindustry-wrapper'>
              <label className='expert-form__label'>Team</label>
              <div className='expert-form__input-wrapper'>
                <div className='invite-expert__note'>
                  Make sure to select your team, the event will be added to team
                  calendar!
                </div>

                <Field
                  name='team'
                  placeholder='Select team'
                  options={this.getTeamOptions()}
                  itemToString={item => item && item.name}
                  component={Dropdown}
                />
              </div>
            </div>

            <Field
              name='clients'
              label='To client contacts'
              itemToString={item => item && item.fullName}
              options={this.clients}
              component={TagsInput}
            />

            <Field
              name='attendees'
              label='To additional emails'
              component={TagsInput}
              itemToString={item => item && item.fullName}
              loadOptions={query =>
                employeeApi.typeaheadSearch(employeePositions, query)
              }
              creatable
              itemFromCreatable={email => ({
                email: email.trim(),
                fullName: email.trim()
              })}
            />

            <Field name='subject' label='Subject' component={Input} />

            <Field name='location' label='Location' component={Input} />

            <div className='expert-form__input-group'>
              <label className='expert-form__label'>Starts on</label>
              <div className='expert-form__input-wrapper'>
                {schedule && formatDateTime(schedule.startTime)}
              </div>
            </div>

            <div className='expert-form__input-group'>
              <label className='expert-form__label'>Ends on</label>
              <div className='expert-form__input-wrapper'>
                {schedule && formatDateTime(schedule.endTime)}
              </div>
            </div>

            <Field name='message' label='Message' component={RichTextEditor} />

            <Field
              name='attachExpertProfilePdf'
              label='Attach expert profile as PDF'
              component={Checkbox}
            />

            <div className='invite-expert__info m-t-lg'>
              <InfoIcon />
              <div>
                This is a calendar invite for client contacts on the project.{' '}
                <br />
                Your team calendar will be updated as well.
              </div>
            </div>

            <hr className='hr-separator hr-separator--light hr-separator--large-margin' />

            {this.renderControls(form)}
          </Form>
        )}
      />
    )
  }
}

InviteClient.defaultProps = {
  shouldCloseOnOverlayClick: true,
  hideButtons: true,
  showCloseIcon: false
}

InviteClient.propTypes = {
  growlStore: propTypes.object,
  staticData: propTypes.object,
  modalStore: propTypes.object,
  userStore: propTypes.object,
  epl: propTypes.object,
  eplModel: propTypes.object,
  project: propTypes.object,
  schedule: propTypes.object
}

export default inject('growlStore', 'staticData', 'modalStore', 'userStore')(
  observer(InviteClient)
)
